import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { AgentHealth, fetchAgentHealth } from '../../services/fleetHudApi';

type HealthState = 'loading' | 'error' | AgentHealth;

function verdictClass(verdict: string): string {
  switch (verdict) {
    case 'healthy':
      return 'text-text-secondary';
    case 'attention':
      return 'text-brand';
    case 'silent':
      return 'text-red-500';
    default:
      return 'text-text-muted';
  }
}

interface FleetHealthProps {
  /** Bridge right-rail density (item 6): 32px rows, tighter padding. */
  dense?: boolean;
  /** Inside a RailBlock the block renders the panel title — suppress ours. */
  hideHeader?: boolean;
  className?: string;
}

export default function FleetHealth({ dense = false, hideHeader = false, className }: FleetHealthProps) {
  const [open, setOpen] = useState(false);
  const [health, setHealth] = useState<HealthState | null>(null);

  // Layout-only switches — no logic or data-fetch behaviour changes.
  const padY = dense ? 'py-0.5' : 'py-2';
  const rowH = dense ? ' h-8' : '';

  const toggle = () => {
    const next = !open;
    setOpen(next);
    // Fetch only on first expand — never on mount, never on re-expand.
    if (next && health === null) {
      setHealth('loading');
      fetchAgentHealth()
        .then((h) => setHealth(h))
        .catch(() => setHealth('error'));
    }
  };

  return (
    <section className={`rounded-lg border border-hairline bg-surface${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-2 px-5 py-3 text-left"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-text-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 text-text-muted" />
        )}
        {!hideHeader && (
          <h2 className="text-sm font-semibold tracking-wide text-text-primary">AGENT HEALTH</h2>
        )}
        {health !== null && health !== 'loading' && health !== 'error' && (
          <span className="font-mono text-xs text-text-muted">
            {health.runs_sampled} runs · {health.sessions_sampled} sessions
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-hairline">
          {health === null || health === 'loading' ? (
            <p className="px-5 py-4 text-sm text-text-muted">Loading…</p>
          ) : health === 'error' ? (
            <p className="px-5 py-4 text-sm text-red-500">Agent health unavailable.</p>
          ) : !health.agents || health.agents.length === 0 ? (
            <p className="px-5 py-4 text-sm text-text-muted">
              {health.note ?? 'No agent sessions in the sampled runs.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline text-left text-xs text-text-muted">
                    <th className={`px-5 ${padY} font-medium`}>agent</th>
                    <th className={`px-3 ${padY} font-medium`}>verdict</th>
                    <th className={`px-3 ${padY} font-medium text-right`}>runs</th>
                    <th className={`px-3 ${padY} font-medium text-right`}>passed</th>
                    <th className={`px-3 ${padY} font-medium text-right`}>warned</th>
                    <th className={`px-5 ${padY} font-medium text-right`}>failed</th>
                  </tr>
                </thead>
                <tbody>
                  {health.agents.map((a) => (
                    <tr key={a.agent} className={`border-b border-hairline last:border-b-0${rowH}`}>
                      <td className={`px-5 ${padY} font-mono text-text-primary`}>{a.agent}</td>
                      <td className={`px-3 ${padY} ${verdictClass(a.verdict)}`}>{a.verdict}</td>
                      <td className={`px-3 ${padY} font-mono text-right text-text-secondary`}>{a.runs}</td>
                      <td className={`px-3 ${padY} font-mono text-right text-text-secondary`}>{a.passed}</td>
                      <td className={`px-3 ${padY} font-mono text-right text-text-secondary`}>{a.warned}</td>
                      <td className={`px-5 ${padY} font-mono text-right text-text-secondary`}>{a.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
